import React, { useEffect, useState } from 'react';
import {
  Tabs,
  Tab,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress // Import CircularProgress
} from '@mui/material';

import PictureAsPdfSharpIcon from '@mui/icons-material/PictureAsPdfSharp';
import {
  Description as DescriptionIcon,
  InfoOutlined as InfoOutlinedIcon,
  WarningAmberOutlined as WarningAmberOutlinedIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DOMPurify from 'dompurify';
import PdfJsonViewer from '../PDFViewer/PDFViewerNew';
import ReactJson from 'react-json-view'; // For pretty printing JSON
import fetchData from '../../utils/fetchData';
import { FileIcon as FilePdf, FileJson } from 'lucide-react';
import { API_BASE_URL } from '../../config/config';

const DocumentSummary = () => {
  const [value, setValue] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openJsonDialog, setOpenJsonDialog] = useState(false); // State for JSON dialog
  const [currentPdfLink, setCurrentPdfLink] = useState('');
  const [pdfName, setPdfName] = useState('');
  const [attachmentJsonData, setAttachmentJsonData] = useState({});
  const selectedApplicationData = useSelector(
    state => state.intakeData.selectedApplicationData
  );

  const [documentSummaryData, setDocumentSummaryData] = useState([]);
  const [loading, setLoading] = useState(true); // Add a loading state
  const [warningMessage, setWarningMessage] = useState('');

  //  let documentSummaryData

  useEffect(() => {
    if (selectedApplicationData?.application_id) {
      setLoading(true); // Start loading before fetching data

      fetchData(
        `${API_BASE_URL}/insureai/ml/intake/applications/v2/attachments?application_id=${selectedApplicationData?.application_id}`,
        'GET'
      )
        .then(data => {
          setDocumentSummaryData(data);
        })
        .catch(error => {
          console.error('Error fetching document summary data:', error);
        })
        .finally(() => {
          setLoading(false); // Stop loading regardless of success or failure
        });
    } else {
      setDocumentSummaryData([]); // Reset data when application_id is not available.
      setLoading(false); // Ensure loading is false if no data is fetched
    }
  }, [selectedApplicationData]);

  const handleEditButton = () => {
    setOpenEditDialog(true);
    setOpenDialog(false);
  };

  const handlePdfClick = async (pdfLink, pdfName) => {
    setCurrentPdfLink(pdfLink);
    setOpenDialog(true); // Open dialog after setting the link
    setPdfName(pdfName);
    setAttachmentJsonData({});
  };

  const handleJsonClick = (docId, pdfName) => {
    setOpenJsonDialog(true);
    setPdfName(pdfName);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleEditDialogClose = () => {
    setOpenEditDialog(false);
  };

  const handleJsonDialogClose = () => {
    // Function to close JSON dialog
    setOpenJsonDialog(false);
  };

  const closeDialog = () => {
    setOpenDialog(false);
    setOpenEditDialog(false);
    setOpenJsonDialog(false); // Close JSON dialog as well
  };

  const handleTabChange = (event, newValue) => {
    setValue(newValue);
    let url = ``;
    if (documentSummaryData[newValue]?.is_application) {
      url = `${API_BASE_URL}/application_mapping/mappings/get_consolidated_json?doc_id=${documentSummaryData[newValue]?.doc_id}&application_id=${selectedApplicationData?.application_id}`;
    } else {
      url = `${API_BASE_URL}/insureai/ml/intake/applications/v2/detailed_json?doc_id=${documentSummaryData[newValue]?.doc_id}`;
    }
    fetchData(url, 'GET')
      .then(data => {
        setAttachmentJsonData(data?.[0]?.json || data);
      })
      .catch(error => {
        console.error('Error fetching document summary data:', error);
      });
  };

  useEffect(() => {
    setValue(0);
    handleTabChange(null, 0);
  }, [selectedApplicationData, documentSummaryData]);

  return (
    <>
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '400px'
          }}
        >
          <CircularProgress sx={{ color: '#ea580c' }} />
        </Box>
      ) : (
        documentSummaryData && (
          <div className='document-summary mt-4'>
            <h1 className='mb-2 text-2xl text-orange-600'>Document Summary</h1>
            <Box sx={{ borderColor: 'divider', overflowX: 'auto' }}>
              <Tabs
                value={value}
                onChange={handleTabChange}
                variant='scrollable'
                scrollButtons='auto'
                aria-label='document summary tabs'
                className='12'
              >
                {documentSummaryData?.map((attachment, index) => (
                  <Tab
                    key={index}
                    label={attachment.name}
                    className='tab-view'
                    sx={{ minWidth: '175px' }}
                  />
                ))}
              </Tabs>
            </Box>
            <Box className='p-6 doc-content shadow-md rounded-b-lg'>
              <div className='flex justify-between items-center'>
                <h2 className='font-semibold mb-2'>
                  Summary
                  <button
                    size='sm'
                    className='ml-2 text-sm h-7 px-3 bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 hover:text-blue-800 inline-flex items-center'
                    onClick={() =>
                      handlePdfClick(
                        documentSummaryData[value]?.link,
                        documentSummaryData[value]?.name
                      )
                    }
                  >
                    <FilePdf className='h-3.5 w-3.5 mr-1.5' />
                    View PDF
                  </button>
                  <button
                    size='sm'
                    className='ml-2 text-sm h-7 px-3 bg-green-50 border-green-200 text-green-700 hover:bg-green-100 hover:text-green-800 inline-flex items-center'
                    onClick={() =>
                      handleJsonClick(
                        documentSummaryData[value]?.doc_id,
                        documentSummaryData[value]?.name
                      )
                    }
                  >
                    <FileJson className='h-3.5 w-3.5 mr-1.5' />
                    View JSON
                  </button>
                </h2>
              </div>
              <p
                className='text-gray-700 document-summary'
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    documentSummaryData[value]?.summary || ''
                  )
                }}
              />
            </Box>
            <Dialog
              open={openDialog}
              onClose={handleDialogClose}
              maxWidth='lg'
              fullWidth
              PaperProps={{ sx: { border: '1px solid #ea580c' } }}
            >
              <DialogTitle style={{ color: '#ea580c' }}>
                {pdfName}
                <span onClick={handleDialogClose} className='close-icon'>
                  <CloseIcon />
                </span>
              </DialogTitle>
              <DialogContent>
                {documentSummaryData[value]?.is_application && (
                  <button className='edit-pdf-btn' onClick={handleEditButton}>
                    Edit PDF <PictureAsPdfIcon />
                  </button>
                )}
                <Box sx={{ height: '80vh' }}>
                  <iframe
                    src={currentPdfLink}
                    width='100%'
                    height='100%'
                    title='PDF Viewer'
                    style={{ border: 'none' }}
                  />
                </Box>
              </DialogContent>
            </Dialog>
            <Dialog
              open={openJsonDialog}
              onClose={handleJsonDialogClose}
              maxWidth='lg'
              fullWidth
            >
              <DialogTitle>
                {pdfName} JSON Data
                <span onClick={handleJsonDialogClose} className='close-icon'>
                  <CloseIcon />
                </span>
              </DialogTitle>
              <DialogContent>
                <Box sx={{ height: '65vh', overflowY: 'auto' }}>
                  <ReactJson
                    src={attachmentJsonData || {}}
                    theme='monokai'
                    style={{ fontSize: '14px' }}
                    displayObjectSize={false}
                    displayDataTypes={false}
                  />
                </Box>
              </DialogContent>
            </Dialog>
            <Dialog
              open={openEditDialog}
              onClose={handleEditDialogClose}
              maxWidth='xl'
              fullWidth
              PaperProps={{
                sx: {
                  border: '1px solid #ea580c',
                  height: '95%',
                  width: '95%',
                  maxWidth: 'none',
                  maxHeight: 'none'
                }
              }}
            >
              <DialogTitle style={{ color: '#ea580c', paddingBottom: '10px' }}>
                {pdfName}
                <span onClick={handleEditDialogClose} className='close-icon'>
                  <CloseIcon />
                </span>
              </DialogTitle>
              <DialogContent>
                {warningMessage ? (
                  <Box
                    sx={{
                      mb: 2,
                      p: 1,
                      backgroundColor: '#ea580c26',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <WarningAmberOutlinedIcon
                      sx={{ color: '#ea580c', marginRight: '8px' }}
                    />
                    <p style={{ margin: 0, color: '#ea580c' }}>
                      {warningMessage}
                    </p>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      mb: 1,
                      backgroundColor: '#e3f2fd',
                      borderRadius: '4px',
                      display: 'none',
                      alignItems: 'center',
                      padding: '6px'
                    }}
                  >
                    <InfoOutlinedIcon
                      sx={{ color: '#0d47a1', marginRight: '8px' }}
                    />
                    <p
                      style={{ margin: 0, color: '#0d47a1', fontSize: '14px' }}
                    >
                      Review the document carefully before editing. Click on
                      yellow areas on the PDF to update data in the highlighted
                      panel.
                    </p>
                  </Box>
                )}
                <Box sx={{ height: '85vh' }}>
                  <PdfJsonViewer
                    docId={documentSummaryData[value]?.doc_id}
                    applicationId={selectedApplicationData?.application_id}
                    currentPdfLink={currentPdfLink}
                    closeDialog={closeDialog}
                    setWarningMessage={setWarningMessage}
                  />
                </Box>
              </DialogContent>
            </Dialog>
          </div>
        )
      )}
    </>
  );
};

export default DocumentSummary;
